<template>
  <TirPmContextMenu
    class="taks-parent-view__context-menu"
    ref="$contextMenu"
    min-width="20rem"
    maxHeight="max-content"
    :trigger-buttons="[TirPmContextMenuMouseButtonEnum.Right]"
  >
    <TirPmRouterLink
      :isDisabled="!parentTask.HasAccessToParentTask"
      :class="[
        'taks-parent-view',
        { 'taks-parent-view--no-access': !parentTask.HasAccessToParentTask },
        { 'in-drawer': inDrawer },
      ]"
      :to="computedRoute"
      @mousedown.middle.prevent
    >
      <div class="taks-parent-view__taskid">{{ parentTask.Id }}</div>
      <div class="taks-parent-view__parent">
        <ConnectIcon class="taks-parent-view__parent-icon" />
        <span class="taks-parent-view__parent-text">Родительская задача</span>
      </div>
      <div class="taks-parent-view__name">
        {{ parentTask.Name }}
      </div>
      <div
        class="taks-parent-view__delete-button"
        v-if="showDeleteButton && parentTask.HasAccessToParentTask"
      >
        <div @click.stop.prevent>
          <TirPmPopover
            ref="$confirmUnattachPopover"
            :custom-position="{
              right: '0rem',
              bottom: '0.25rem',
            }"
          >
            <TirPmTooltipElement :position="TirPmTooltipElementPlacementEnum.Left">
              <TirPmButton
                :variant="TirPmButtonVariantEnum.LinkState"
                :size="TirPmButtonSizeEnum.Small"
                :is-loading="isLoadingUnattach"
              >
                <template #leftSlot>
                  <TrashIcon />
                </template>
              </TirPmButton>
              <template #content>Удалить связь </template>
            </TirPmTooltipElement>
            <template #content>
              <div class="taks-parent-view__confirm-popover">
                <span class="taks-parent-view__confirm-popover-message">
                  Открепить родительскую задачу?
                </span>
                <div class="taks-parent-view__confirm-popover-buttons">
                  <TirPmButton
                    :variant="TirPmButtonVariantEnum.PrimaryState"
                    :size="TirPmButtonSizeEnum.Small"
                    :is-loading="isLoadingUnattach"
                    @click.stop="emit('unattach')"
                  >
                    Открепить
                  </TirPmButton>
                  <TirPmButton
                    :variant="TirPmButtonVariantEnum.SecondaryState"
                    :size="TirPmButtonSizeEnum.Small"
                    :is-disabled="isLoadingUnattach"
                    @click.stop="$confirmUnattachPopover?.close()"
                  >
                    Отмена
                  </TirPmButton>
                </div>
              </div>
            </template>
          </TirPmPopover>
        </div>
      </div>
    </TirPmRouterLink>
    <template #content>
      <TirPmContextMenuButton v-if="props.parentTask.HasAccessToParentTask" @click="openDrawer">
        <div class="taks-parent-view__menu-item">
          <CurtainIcon class="taks-parent-view__menu-icon" />
          Открыть в шторке
        </div>
      </TirPmContextMenuButton>
      <RouterLink :to="computedRoute" target="_blank" v-if="props.parentTask.HasAccessToParentTask">
        <TirPmContextMenuButton>
          <div class="taks-parent-view__menu-item">
            <ArrowsPointingOutIcon class="taks-parent-view__menu-icon" />
            Открыть в новой вкладке
          </div>
        </TirPmContextMenuButton>
      </RouterLink>
      <RouterLink :to="openSchema">
        <TirPmContextMenuButton>
          <div class="taks-parent-view__menu-item">
            <MapIcon class="taks-parent-view__menu-icon" />
            Посмотреть карту проекта
          </div>
        </TirPmContextMenuButton>
      </RouterLink>
      <TirPmContextMenuButton @click="copyNameAndLinkTask">
        <div class="taks-parent-view__menu-item">
          <ShareIcon class="taks-parent-view__menu-icon" />
          Скопировать название и ссылку
        </div>
      </TirPmContextMenuButton>
    </template>
  </TirPmContextMenu>
  <TaskDrawer ref="$taskDrawer" />
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType, ref } from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmPopover,
  TirPmTooltipElementPlacementEnum,
  TirPmTooltipElement,
  TirPmContextMenu,
  TirPmContextMenuMouseButtonEnum,
  TirPmContextMenuButton,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmRouterLink,
} from 'tir-components';
import { RouteLocationRaw, RouterLink, useRouter } from 'vue-router';
import {
  ConnectIcon,
  TrashIcon,
  CurtainIcon,
  ArrowsPointingOutIcon,
  MapIcon,
  ShareIcon,
} from 'tir-style-system/icons/outline';
import { TaskParentModel } from '../../models';
import { TaskDrawer } from '../task-drawer';
import { TaskDrawerSettingsModel } from '../task-drawer';
import { copyToClipboard } from '../../utils';

/**Роутер */
const router = useRouter();

const emit = defineEmits<{
  /**Событие открепления */
  (e: 'unattach'): void;
}>();

const props = defineProps({
  /**Родительская задача */
  parentTask: {
    type: Object as PropType<TaskParentModel>,
    required: true,
  },
  /**Загрузка при откреплении */
  isLoadingUnattach: {
    type: Boolean,
    default: false,
  },
  /**В шторке */
  inDrawer: {
    type: Boolean,
    default: false,
  },
  /**Показывать кнопку удаления */
  showDeleteButton: {
    type: Boolean,
    default: true,
  },
});

/**Ссылка на компонент контекстного меню */
const $contextMenu = ref<InstanceType<typeof TirPmContextMenu>>();
/**Ссылка на компонент шторки задачи */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();
/**Ссылка на компонент поповера подтверждения при откреплении задачи */
const $confirmUnattachPopover = ref<InstanceType<typeof TirPmPopover>>();

/**Вычисление маршрута до задачи */
const computedRoute = computed(() => {
  return router.resolve(getTaskRoute())?.href;
});

/**Получить роут на задачу */
const getTaskRoute = (): RouteLocationRaw => {
  return {
    name: 'task-detail',
    params: {
      projectId: props.parentTask.ProjectId,
      namespaceId: props.parentTask.NamespaceId,
      queueId: props.parentTask.QueueId,
      taskId: props.parentTask.Id,
    },
  };
};

/**Открыть задачу в шторке */
const openDrawer = () => {
  $contextMenu.value?.close();
  $taskDrawer.value?.open(
    new TaskDrawerSettingsModel({
      Mode: 'edit',
      TaskId: props.parentTask.Id,
    }),
  );
};
/**Открыть карту проекта */
const openSchema = computed(() => {
  return router.resolve({
    name: 'project-map',
    params: {
      taskId: props.parentTask.Id,
      projectId: props.parentTask.ProjectId,
    },
  });
});
/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};
/**Скопировать название и ссылку */
const copyNameAndLinkTask = async () => {
  const routePath = router.resolve(getTaskRoute())?.href;
  if (!routePath) return;

  const origin =
    window.location.hostname === 'localhost' ? 'https://beta.hub-pm.com' : window.location.origin;

  const link = origin + routePath;
  const textContent = `${props.parentTask.Id} ${props.parentTask.Name}`;

  if (await copyToClipboard(`[${textContent}](${link})`)) {
    $contextMenu.value.close();
    addNotification('Название и ссылка тикета скопирована');
  }
};
</script>

<style lang="scss" scoped>
.taks-parent-view {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border-radius: 0.5rem;
  border: 0.063rem solid var(--neutral-translucent-10, rgba(39, 45, 55, 0.1));
  background: var(--bg-tertiary, #f4f4f5);
  padding: 0.5rem 0.625rem 0.5rem 0.625rem;
  cursor: pointer;

  &--no-access {
    cursor: not-allowed !important;
  }

  &__context-menu {
    width: 100%;
    max-width: calc(100% - 92px);
  }

  &.in-drawer {
    padding: 0 0.625rem;
    border-color: var(--neutral-translucent-10, rgba(39, 45, 55, 0.1));
    background: var(--bg-base, #fff);
    min-height: 2rem;
  }

  &__taskid {
    color: var(--text-primary, #272d37);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 5rem;
  }
  &__parent {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--text-secondary, #686c73);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 15rem;
  }
  &__parent-icon {
    width: 1rem;
    min-width: 1rem;
    height: 1rem;
  }
  &__parent-text {
    color: var(--text-secondary, #686c73);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__delete-button {
    margin-left: auto;
  }
  &__name {
    color: var(--text-primary, #272d37);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    white-space: nowrap;
    width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__confirm-popover {
    border-radius: 0.5rem;
    background: var(--bg-base, #fff);
    padding: var(--12, 0.75rem);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  &__confirm-popover-message {
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5rem;
  }
  &__confirm-popover-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  &__menu-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__menu-icon {
    width: 1.5rem;
    min-width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
